/**
 * Version-drift detection — the proactive half of the stale-chunk
 * defence.
 *
 * The reactive half (`lib/stale-chunk.ts`) catches a tab that has
 * ALREADY tried to import a chunk the deploy rotated away and reloads
 * once. That recovers from the crash, but the user spent a second
 * staring at the crash screen first.
 *
 * This module runs in front of that: it polls `/version.json` (emitted
 * by the `dashboard-v2:version-manifest` Vite plugin and served with
 * the default `/*` `Cache-Control: no-cache`, so it always revalidates)
 * and compares the server-reported build SHA to the SHA inlined into
 * THIS bundle at build time (`import.meta.env.PUBLIC_BUILD_SHA`). If
 * they differ, the tab is older than the deployed code — call the
 * supplied `onDrift` callback so the UI can prompt the user to refresh.
 *
 * Polling cadence:
 *
 *   - Once on start (fires from the React mount).
 *   - On `visibilitychange` to `visible` — long-lived background tabs
 *     check as soon as they get focus again. This is when stale chunks
 *     usually bite, so it's also when we want to know.
 *
 * Both are debounced through `POLL_DEBOUNCE_MS` so a quick tab-flick
 * doesn't hammer the endpoint. Hidden tabs skip the fetch entirely.
 *
 * Once drift is reported the watcher stops — the user knows; firing
 * again on every subsequent visibility flip would be noise. A real
 * fresh page load resets the module state.
 *
 * Local dev (`PUBLIC_BUILD_SHA` unset / `'dev'`) is a no-op: every
 * vite-dev reload changes the bundle and there's no meaningful
 * server-side comparison to make.
 */

const PATH = '/version.json';
const POLL_DEBOUNCE_MS = 60_000;

interface VersionPayload {
  version: string;
}

export interface VersionDriftInfo {
  serverVersion: string;
  clientVersion: string;
}

export interface StartVersionCheckOptions {
  /** Called exactly once when the server's `/version.json` reports a
   *  different version than the running bundle. The callback is
   *  responsible for any user-facing UI (toast, banner, etc.). */
  onDrift: (info: VersionDriftInfo) => void;
  /** Optional override for testing. Defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Optional override for the visibility check. Defaults to reading
   *  `document.visibilityState`. */
  isVisible?: () => boolean;
  /** Optional override for the client-side version. Defaults to
   *  `import.meta.env.PUBLIC_BUILD_SHA`. */
  clientVersion?: string;
}

/**
 * Read the build SHA that Vite inlined into this bundle. `undefined`
 * (or `'dev'`) means we have no version to compare against — local
 * dev, accidental missing CI var, etc. Callers should treat it as
 * "skip the watcher entirely."
 */
export function resolveClientVersion(): string | undefined {
  const raw = import.meta.env.PUBLIC_BUILD_SHA;
  if (typeof raw !== 'string') return undefined;
  if (raw.length === 0 || raw === 'dev') return undefined;
  return raw;
}

/**
 * Start the version-drift watcher. Returns an unsubscribe function that
 * removes the `visibilitychange` listener; safe to call from a React
 * effect cleanup.
 *
 * If there's no client version to compare against (local dev / SSR /
 * no `window`), this is a no-op and the returned cleanup is also a
 * no-op.
 */
export function startVersionCheck(opts: StartVersionCheckOptions): () => void {
  const clientVersion = opts.clientVersion ?? resolveClientVersion();
  if (!clientVersion || typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {
      /* no-op */
    };
  }

  const fetchImpl = opts.fetchImpl ?? window.fetch.bind(window);
  const isVisible = opts.isVisible ?? (() => document.visibilityState === 'visible');

  let lastPollAt = 0;
  let driftReported = false;
  let cancelled = false;

  const check = async (): Promise<void> => {
    if (cancelled || driftReported) return;
    if (!isVisible()) return;
    const now = Date.now();
    if (now - lastPollAt < POLL_DEBOUNCE_MS) return;
    lastPollAt = now;
    try {
      // Cache-bust the URL even though `_headers` already says
      // `no-cache`, because some CDN edges + browser quirks ignore
      // the header on identical URLs they've recently fetched. The
      // query string is harmless to CF Pages' filename match.
      const res = await fetchImpl(`${PATH}?_=${now}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as Partial<VersionPayload>;
      const serverVersion = data.version;
      if (
        typeof serverVersion !== 'string' ||
        serverVersion.length === 0 ||
        serverVersion === clientVersion
      ) {
        return;
      }
      driftReported = true;
      opts.onDrift({ serverVersion, clientVersion });
    } catch {
      // Network blip, JSON parse failure, CSP block — all benign.
      // The next visibility flip will retry.
    }
  };

  const onVisibility = (): void => {
    if (isVisible()) void check();
  };

  document.addEventListener('visibilitychange', onVisibility);
  // First check fires as soon as the watcher mounts. Wrapped in a
  // microtask so the React tree finishes painting before the fetch
  // goes out — no measurable user impact but keeps the network panel
  // tidy under devtools.
  void Promise.resolve().then(check);

  return () => {
    cancelled = true;
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/** Internal — exposed for tests to reset module-state poll debounce. */
export const __TEST_ONLY_constants = {
  POLL_DEBOUNCE_MS,
  PATH,
};
